/**
 * Chuyển khung thực đơn tháng (MenuPlan) thành dữ liệu cho các ô lọc.
 *
 * Vì sao cần: trước đây mọi màn tự sinh 12 tháng của NĂM DƯƠNG LỊCH hiện tại
 * (`dayjs().startOf('year')`) rồi ghép `searchStr` với năm cứng `dayjs().year()`. Cách đó sai với
 * năm học vì năm học trải hai năm dương lịch: học kỳ 2 là tháng 01..08 của năm SAU. Chọn "tháng 01"
 * mà gửi năm hiện tại là đi tìm dữ liệu của năm trước — sai âm thầm, không báo lỗi.
 *
 * Nay tháng phải lấy từ khung đã `confirmed` của MenuPlan, và mỗi lựa chọn mang theo `month_key`
 * (`YYYY-MM`) nên năm luôn đi kèm tháng.
 *
 * Tách khỏi component để unit test được (vitest chạy environment 'node').
 */
import type { MonthlyMenuFrame } from 'common/types/menuPlan';

export interface MonthOption {
  /** `YYYY-MM` — dùng luôn làm value, nhờ vậy năm không bao giờ bị tách khỏi tháng. */
  id: string;
  /** Nhãn hiển thị `MM/YYYY`. */
  name: string;
}

export interface WeekOption {
  id: number;
  name: string;
  date_from: string;
  date_to: string;
  /** Có khi MenuPlan gửi. Không có thì tiêu đề năm học rơi về tuần theo tháng. */
  school_year_week?: number;
}

/** Tách `YYYY-MM` thành số năm/tháng. Trả null nếu chuỗi không đúng dạng. */
export const parseMonthKey = (monthKey?: string | null): { year: number; month: number } | null => {
  if (!monthKey) return null;
  const matched = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!matched) return null;

  const year = Number(matched[1]);
  const month = Number(matched[2]);
  if (month < 1 || month > 12) return null;

  return { year, month };
};

/** `month_start` có nằm trong đúng tháng mà `month_key` gọi tên? */
const startsInOwnMonth = (frame: MonthlyMenuFrame): boolean =>
  (frame.month_start || '').slice(0, 7) === frame.month_key;

/**
 * Gộp các khung trùng `month_key`.
 *
 * Thực tế sẽ KHÔNG có hai khung cùng tháng — dev đang trùng vì dữ liệu test. Nhưng để nguyên cả
 * hai thì ô Tháng hiện "09/2026" hai lần, nên vẫn phải gộp.
 *
 * Chọn cái nào: ưu tiên khung có `month_start` nằm trong ĐÚNG tháng của `month_key`. Dev có hai
 * khung `2026-09`: một cái `month_start` 07/09 (4 tuần) và một cái 31/08 (5 tuần). Tháng 9 chỉ có
 * 4 tuần, nên cái bắt đầu từ 31/08 là rác của năm học trước. Trước đây tôi chọn theo `id` lớn hơn
 * — một quy tắc tự nghĩ ra, và nó trúng đúng khung 5 tuần, làm ô Tuần ăn hiện 5 tuần và ô Ngày ăn
 * hiện ra 31/08.
 *
 * `id` lớn hơn chỉ còn là tiêu chí cuối, khi hai khung cùng trạng thái. Không có trùng thì hàm này
 * không đổi gì cả.
 */
export const dedupeFramesByMonth = (frames: MonthlyMenuFrame[]): MonthlyMenuFrame[] => {
  const chosen = new Map<string, MonthlyMenuFrame>();
  frames.forEach((frame) => {
    const current = chosen.get(frame.month_key);
    if (!current) {
      chosen.set(frame.month_key, frame);
      return;
    }
    const a = startsInOwnMonth(frame);
    const b = startsInOwnMonth(current);
    if (a !== b) {
      if (a) chosen.set(frame.month_key, frame);
      return;
    }
    if (frame.id > current.id) chosen.set(frame.month_key, frame);
  });
  // Giữ thứ tự xuất hiện đầu tiên của mỗi tháng để không xáo trộn thứ tự MenuPlan đã sắp.
  return frames.filter((frame) => chosen.get(frame.month_key) === frame);
};

/** Số tháng của một năm học: 12 tháng, mở đầu bằng tháng 9. */
export const SCHOOL_YEAR_MONTH_COUNT = 12;
/** Năm học mở đầu ở tháng 9 dương lịch. */
export const SCHOOL_YEAR_FIRST_MONTH = 9;

/** Năm học N: 01/09/N → 31/08/N+1 (học kỳ 1 rồi học kỳ 2). */
export const schoolYearDateRange = (schoolYear: number): { from: string; to: string } => ({
  from: `${schoolYear}-${String(SCHOOL_YEAR_FIRST_MONTH).padStart(2, '0')}-01`,
  to: `${schoolYear + 1}-08-31`,
});

/**
 * Năm học hiện hành suy từ lịch hệ thống (docx 2.6: "căn cứ tháng và năm hệ thống hiện hành, hệ
 * thống suy ra Năm học hiện hành"). Từ tháng 9 trở đi là năm học của chính năm đó, trước tháng 9
 * vẫn là năm học trước.
 *
 * PHẢI tự suy rồi truyền `year` sang MenuPlan: tham số `year` của họ mặc định là NĂM DƯƠNG LỊCH
 * hôm nay, nên từ tháng 1 đến tháng 8 sẽ hỏi nhầm sang năm học sau và trả về rỗng.
 *
 * Docx nói nguồn chuẩn là bảng Năm kinh doanh của KIOS; KIOS dev chưa có API nào cho việc này nên
 * tạm suy tại chỗ theo đúng quy tắc trên.
 */
export const deriveSchoolYear = (calendarYear: number, calendarMonth: number): number =>
  calendarMonth >= SCHOOL_YEAR_FIRST_MONTH ? calendarYear : calendarYear - 1;

const toMonthOption = (year: number, month: number): MonthOption => {
  const mm = String(month).padStart(2, '0');
  return { id: `${year}-${mm}`, name: `${mm}/${year}` };
};

/**
 * Đủ 12 tháng của một năm học, KHÔNG phụ thuộc tháng nào đã chốt khung.
 *
 * Năm học N chạy T9/N → T8/N+1 (docx 2.6 §Quản lý kỳ kinh doanh: "tất cả các combo chọn tháng sẽ
 * tuân thủ cấu trúc [Từ tháng 9/N-1 đến tháng 8/N]"). Danh sách tháng theo KỲ KINH DOANH, còn việc
 * MenuPlan đã chốt khung hay chưa chỉ quyết định ô "Tuần ăn" có gì. Nếu chỉ liệt kê tháng đã có
 * khung thì đầu năm học combo gần như rỗng, người dùng tưởng hỏng.
 *
 * Ngoại lệ tháng 8: đang tháng 8 thì cho xem thêm T9 của năm học sau, vì MenuPlan đẩy khung tháng 9
 * sang từ tháng 8 ("riêng tháng 8, Gate sẽ cho xem dữ liệu thêm Tháng 9, năm học 26-27").
 */
export const buildSchoolYearMonthOptions = (
  schoolYear?: number | null,
  currentCalendarMonth?: number
): MonthOption[] => {
  if (!schoolYear || !Number.isInteger(schoolYear)) return [];

  const months = Array.from({ length: SCHOOL_YEAR_MONTH_COUNT }, (_, index) => {
    const offset = SCHOOL_YEAR_FIRST_MONTH - 1 + index; // 8..19
    return toMonthOption(schoolYear + Math.floor(offset / 12), (offset % 12) + 1);
  });

  if (currentCalendarMonth === SCHOOL_YEAR_FIRST_MONTH - 1) {
    months.push(toMonthOption(schoolYear + 1, SCHOOL_YEAR_FIRST_MONTH));
  }
  return months;
};

/**
 * Tháng chọn sẵn: tháng hiện tại nếu nó thuộc năm học đang xem, không thì tháng mới nhất ĐÃ có
 * khung (chỗ chắc chắn có dữ liệu để xem), cuối cùng mới lấy tháng đầu năm học.
 */
export const pickDefaultMonthKey = (
  options: MonthOption[],
  frames: MonthlyMenuFrame[],
  todayMonthKey: string
): string | undefined => {
  if (options.length === 0) return undefined;
  if (options.some((o) => o.id === todayMonthKey)) return todayMonthKey;

  const framed = frames
    .map((f) => f.month_key)
    .filter((key) => options.some((o) => o.id === key))
    .sort();

  return framed[framed.length - 1] ?? options[0].id;
};

/**
 * Lựa chọn cho ô "Tuần ăn" của một tháng — lấy từ `week_ranges` của khung, KHÔNG tự tính theo
 * dayjs: khung không trùng tháng dương lịch (vd khung 2026-09 chạy 07/09 → 04/10) nên tuần tự tính
 * sẽ lệch kỳ.
 */
export const toWeekOptions = (frame?: MonthlyMenuFrame | null): WeekOption[] =>
  (frame?.week_ranges ?? []).map((range) => ({
    id: range.week_number,
    name: `Tuần ${range.week_number}`,
    date_from: range.date_from,
    date_to: range.date_to,
    ...(typeof range.school_year_week === 'number'
      ? { school_year_week: range.school_year_week }
      : {}),
  }));

export const findFrame = (
  frames: MonthlyMenuFrame[],
  monthKey?: string | null
): MonthlyMenuFrame | null => frames.find((f) => f.month_key === monthKey) ?? null;

/**
 * Tháng nên dùng sau khi danh sách khung đổi.
 *
 * Danh sách tháng nay do MenuPlan quyết định nên tháng đang chọn có thể biến mất (khung bị gỡ, đổi
 * năm học, hoặc lần đầu chưa chọn gì). Khi đó phải rơi về khung mặc định thay vì giữ một giá trị
 * không còn trong danh sách — select sẽ hiện rỗng còn API vẫn bị gọi với tháng cũ.
 */
export const resolveMonthKey = (
  current: string | undefined,
  options: MonthOption[],
  defaultMonthKey?: string
): string | undefined =>
  current && options.some((o) => o.id === current) ? current : defaultMonthKey;

/**
 * Tuần nên dùng sau khi đổi tháng.
 *
 * Số tuần khác nhau giữa các khung (khung 4 tuần, khung 5 tuần) nên tuần 5 của tháng trước có thể
 * không tồn tại ở tháng vừa chọn. `''` nghĩa là "tất cả các tuần" — hợp lệ, đừng đụng vào.
 *
 * `hasMonth` phân biệt hai cảnh giống nhau trên màn hình mà xử lý phải khác: chưa chọn tháng (giữ
 * nguyên, chờ) với đã chọn một tháng CHƯA chốt khung (phải bỏ tuần cũ, vì tuần đó không thuộc
 * tháng này — giữ lại là gọi API bằng một tuần không tồn tại).
 */
export const resolveWeek = <T extends number | '' | undefined>(
  current: T,
  options: WeekOption[],
  hasMonth: boolean
): T | number | undefined => {
  if (current === '') return current;
  if (!hasMonth) return current;
  if (options.length === 0) return undefined;
  return options.some((o) => o.id === current) ? current : options[0].id;
};

/**
 * `searchStr` cho các API resource, dạng `year-month-week-day-state` mà BE/KIOS quy định.
 * Năm lấy TỪ `monthKey`, không lấy `dayjs().year()` — đây chính là chỗ code cũ sai với năm học.
 * Trả null khi chưa chọn tháng (chỗ gọi tự quyết định không gọi API).
 */
export const buildResourceSearchStr = (params: {
  monthKey?: string | null;
  week?: number | string;
  state?: string;
  day?: number | string;
}): string | null => {
  const parsed = parseMonthKey(params.monthKey);
  if (!parsed) return null;

  const segments: (string | number)[] = [parsed.year, parsed.month];
  if (params.week !== undefined && params.week !== '') segments.push(params.week);
  if (params.day !== undefined && params.day !== '') segments.push(params.day);
  if (params.state !== undefined && params.state !== '') segments.push(params.state);

  return segments.join('-');
};
